import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import {
  FiTerminal,
  FiX,
  FiMinimize2,
  FiMaximize2,
  FiTrash2,
  FiCopy,
  FiPlay,
  FiPause,
  FiChevronRight,
  FiSettings,
  FiDownload,
  FiUpload,
  FiChevronUp,
  FiChevronDown
} from 'react-icons/fi'
import { VscTerminal, VscDebugStart, VscDebugStop, VscClearAll } from 'react-icons/vsc'
import { IoMdFlash, IoMdClose } from 'react-icons/io'
import { FaFire } from 'react-icons/fa'
import toast from 'react-hot-toast'

const Terminal = ({ output = '', onClear, onRunCode, onClose }) => {
  const [input, setInput] = useState('')
  const [commandHistory, setCommandHistory] = useState([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [isRunning, setIsRunning] = useState(false)
  const [terminalSize, setTerminalSize] = useState('medium')
  const [showTerminal, setShowTerminal] = useState(true)
  const [outputLines, setOutputLines] = useState([])
  const terminalRef = useRef(null)
  const inputRef = useRef(null)

  const commands = {
    help: {
      description: 'Show available commands',
      execute: () => `Available commands:\n${Object.entries(commands)
        .map(([cmd, data]) => `  ${cmd.padEnd(15)} - ${data.description}`)
        .join('\n')}`
    },
    clear: {
      description: 'Clear terminal output',
      execute: () => {
        onClear?.()
        return 'Terminal cleared.'
      }
    },
    echo: {
      description: 'Print text to terminal',
      execute: (args) => args.join(' ')
    },
    ls: {
      description: 'List files and directories',
      execute: () => 'README.md\nindex.js\npackage.json\nsrc/\npublic/\nnode_modules/'
    },
    pwd: {
      description: 'Print working directory',
      execute: () => '/home/codefire/projects'
    },
    date: {
      description: 'Show current date',
      execute: () => new Date().toLocaleDateString()
    },
    time: {
      description: 'Show current time',
      execute: () => new Date().toLocaleTimeString()
    },
    theme: {
      description: 'Change theme (dark/light)',
      execute: (args) => {
        if (args[0] === 'dark' || args[0] === 'light') {
          const event = new CustomEvent('change-theme', { detail: args[0] })
          window.dispatchEvent(event)
          return `Theme switched to ${args[0]} mode.`
        }
        return 'Usage: theme [dark|light]'
      }
    },
    run: {
      description: 'Run current code',
      execute: () => {
        setIsRunning(true)
        onRunCode?.()
        return 'Running code... ⚡'
      }
    },
    codefire: {
      description: 'Show CodeFire info',
      execute: () => `🔥 CodeFire Terminal v1.0\nProfessional Code Editor with Red Theme\nType 'help' for available commands`
    },
    status: {
      description: 'Show system status',
      execute: () => `System Status:\n  Memory: 256MB/512MB\n  CPU: 12%\n  Storage: 1.2GB/5GB\n  Uptime: ${Math.floor(Math.random() * 100)}h`
    }
  }

  useEffect(() => {
    if (output) {
      const lines = output.split('\n').filter(line => line.trim())
      setOutputLines(prev => [...prev, ...lines])
    }
  }, [output])

  useEffect(() => {
    // Auto-scroll to bottom
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight
    }
  }, [outputLines])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!input.trim()) return

    // Add to command history
    const newHistory = [...commandHistory, input]
    setCommandHistory(newHistory)
    setHistoryIndex(-1)

    // Parse command
    const [cmd, ...args] = input.trim().split(' ')
    const command = cmd.toLowerCase()

    // Add command to output
    addToOutput(`$ ${input}`)

    // Execute command
    if (commands[command]) {
      try {
        const result = commands[command].execute(args)
        addToOutput(result)
      } catch (error) {
        addToOutput(`Error: ${error.message}`)
      }
    } else {
      addToOutput(`Command not found: ${command}. Type 'help' for available commands.`)
    }

    setInput('')
  }

  const addToOutput = (text) => {
    const lines = text.split('\n').map(line => line.trim())
    setOutputLines(prev => [...prev, ...lines])
  }

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (commandHistory.length > 0) {
        const index = historyIndex < 0 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1)
        setHistoryIndex(index)
        setInput(commandHistory[index])
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (commandHistory.length > 0 && historyIndex >= 0) {
        const index = historyIndex < commandHistory.length - 1 ? historyIndex + 1 : -1
        setHistoryIndex(index)
        setInput(index === -1 ? '' : commandHistory[index])
      }
    } else if (e.key === 'Tab') {
      e.preventDefault()
      // Auto-complete
      const currentInput = input.toLowerCase()
      const matchingCommands = Object.keys(commands).filter(cmd => 
        cmd.startsWith(currentInput)
      )
      if (matchingCommands.length === 1) {
        setInput(matchingCommands[0])
      }
    } else if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault()
      handleClear()
    }
  }

  const handleRun = () => {
    setIsRunning(true)
    toast.success('Running code... ⚡')
    onRunCode?.()
    
    // Simulate running
    setTimeout(() => {
      setIsRunning(false)
    }, 2000)
  }

  const handleClear = () => {
    setOutputLines([])
    onClear?.()
    toast.success('Terminal cleared')
  }

  const handleCopy = () => {
    const text = outputLines.join('\n')
    navigator.clipboard.writeText(text)
    toast.success('Terminal output copied! 📋')
  }

  const handleToggleSize = () => {
    const sizes = ['small', 'medium', 'large', 'full']
    const currentIndex = sizes.indexOf(terminalSize)
    const nextIndex = (currentIndex + 1) % sizes.length
    setTerminalSize(sizes[nextIndex])
  }

  const handleClose = () => {
    setShowTerminal(false)
    onClose?.()
  }

  const handleToggleTerminal = () => {
    setShowTerminal(!showTerminal)
  }

  const sizeClasses = {
    small: 'h-48',
    medium: 'h-64',
    large: 'h-96',
    full: 'h-[calc(100vh-100px)]'
  }

  if (!showTerminal) {
    return (
      <div className="fixed bottom-4 right-4 z-40">
        <button
          onClick={handleToggleTerminal}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-red-600 text-white rounded-lg shadow-lg hover:shadow-xl transition-all"
        >
          <FiTerminal />
          <span>Show Terminal</span>
        </button>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`fixed bottom-0 left-0 right-0 z-50 bg-gray-900 ${sizeClasses[terminalSize]} transition-all duration-300 rounded-t-xl shadow-2xl border-t border-gray-800`}
    >
      {/* Terminal Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700 rounded-t-xl">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <VscTerminal className="text-green-400 text-lg" />
            <span className="font-mono text-sm font-medium text-gray-100">Terminal</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${isRunning ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
            <span className="text-xs text-gray-400">
              {isRunning ? 'Running...' : 'Ready'}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Size Controls */}
          <div className="hidden sm:flex items-center space-x-1">
            <button
              onClick={handleToggleSize}
              className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded"
              title={`Size: ${terminalSize}`}
            >
              {terminalSize === 'small' ? 'S' : 
               terminalSize === 'medium' ? 'M' : 
               terminalSize === 'large' ? 'L' : 'F'}
            </button>
          </div>

          {/* Action Buttons */}
          <button
            onClick={handleRun}
            disabled={isRunning}
            className={`p-1.5 rounded ${isRunning ? 'bg-yellow-500/20 text-yellow-400' : 'hover:bg-gray-700 text-gray-400'}`}
            title={isRunning ? 'Running...' : 'Run Code'}
          >
            {isRunning ? <FiPause className="w-4 h-4" /> : <FiPlay className="w-4 h-4" />}
          </button>
          
          <button
            onClick={handleClear}
            className="p-1.5 hover:bg-gray-700 rounded text-gray-400"
            title="Clear Terminal"
          >
            <VscClearAll className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleCopy}
            className="p-1.5 hover:bg-gray-700 rounded text-gray-400"
            title="Copy Output"
          >
            <FiCopy className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleToggleTerminal}
            className="p-1.5 hover:bg-gray-700 rounded text-gray-400"
            title="Minimize"
          >
            <FiChevronDown className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-red-500/20 rounded text-red-400"
            title="Close Terminal"
          >
            <IoMdClose className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Terminal Output */}
      <div
        ref={terminalRef}
        className="flex-1 overflow-y-auto font-mono text-sm p-4 space-y-1 terminal-scrollbar"
      >
        {/* Welcome Message */}
        <div className="text-green-400 mb-2">
          $ Welcome to CodeFire Terminal 🔥
        </div>
        <div className="text-gray-400 mb-4">
          Type 'help' for available commands
        </div>
        
        {/* Output Lines */}
        {outputLines.map((line, index) => (
          <div key={index} className={`${line.startsWith('$') ? 'text-green-400' : 'text-gray-300'}`}>
            {line}
          </div>
        ))}

        {/* Command Prompt */}
        <form onSubmit={handleSubmit} className="flex items-center space-x-2 mt-4">
          <span className="text-green-400 flex-shrink-0">$</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent border-none outline-none text-gray-100 placeholder-gray-500 focus:ring-0 px-2 py-1"
            placeholder="Type command..."
            autoFocus
          />
        </form>
      </div>

      {/* Terminal Footer */}
      <div className="px-4 py-2 bg-gray-800 border-t border-gray-700 flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center space-x-4 overflow-x-auto">
          <div className="flex items-center space-x-2 shrink-0">
            <IoMdFlash className="text-yellow-400" />
            <span>CodeFire Terminal v1.0</span>
          </div>
          <div className="hidden md:block shrink-0">
            <span>↑↓ History • Tab Auto-complete • Enter Execute • Ctrl+L Clear</span>
          </div>
        </div>
        <div className="flex items-center space-x-3 shrink-0">
          <span className="hidden sm:inline">Lines: {outputLines.length}</span>
          <span>•</span>
          <span>Ready</span>
        </div>
      </div>
    </motion.div>
  )
}

export default Terminal